import { useState, useEffect } from 'react'
import { X, Edit2, Trash2 } from 'lucide-react'
import { Card, Badge, Button, Input, Select, SearchBar, Divider, Tabs, ConfirmDialog, Modal } from '../../../shared/components/UI'
import { operatorsApi, unitsApi } from '../../../shared/lib/api'

const empty = { name: '', type: 'spindle', unitId: '', mobile: '' }

export default function OperatorsPage() {
  const [operators, setOperators] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('All')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(empty)
  const [deletingId, setDeletingId] = useState(null)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      operatorsApi.list(),
      unitsApi.list()
    ]).then(([opRes, unitRes]) => {
      setOperators(opRes.data.data)
      setUnits(unitRes.data.data)
    }).finally(() => setLoading(false))
  }, [])

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const visible = operators.filter((o) => {
    const matchTab = tab === 'All' || o.type === tab.toLowerCase()
    const matchSearch = o.name.toLowerCase().includes(search.toLowerCase()) ||
      (o.unit?.name || '').toLowerCase().includes(search.toLowerCase())
    return matchTab && matchSearch
  })

  const startNew = () => {
    setEditing('new')
    setForm({ ...empty, unitId: units[0]?.id || '' })
  }

  const startEdit = (o) => {
    setEditing(o.id)
    setForm({ name: o.name, type: o.type, unitId: o.unit_id, mobile: o.mobile || '' })
  }

  const cancel = () => {
    setEditing(null)
    setForm(empty)
  }

  const save = async () => {
    if (!form.name.trim() || !form.unitId) return

    const payload = {
      name: form.name.trim(),
      type: form.type,
      unitId: form.unitId,
      mobile: form.mobile,
      status: 'active'
    }

    setActing(true)
    setError('')
    try {
      const res = await (editing === 'new'
        ? operatorsApi.create(payload)
        : operatorsApi.update(editing, payload))
      
      const saved = res.data.data
      if (editing === 'new') {
        setOperators((p) => [...p, saved])
      } else {
        setOperators((p) => p.map((o) => o.id === editing ? saved : o))
      }
      cancel()
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed.')
      console.error(err)
    } finally {
      setActing(false)
    }
  }

  const toggleStatus = async (id) => {
    try {
      const res = await operatorsApi.toggleStatus(id)
      const { status } = res.data.data
      setOperators((p) => p.map((o) => o.id === id ? { ...o, status } : o))
    } catch (err) {
      console.error('Failed to toggle operator status:', err)
    }
  }

  const handleDelete = async () => {
    if (!deletingId) return
    setActing(true)
    setError('')
    try {
      await operatorsApi.delete(deletingId)
      setOperators(p => p.filter(o => o.id !== deletingId))
      setDeletingId(null)
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed.')
    } finally {
      setActing(false)
    }
  }

  const TYPE_BADGE = { spindle: 'blue', conning: 'teal' }
  const TYPE_LABEL = { spindle: 'Spindle', conning: 'Conning' }

  const spindleCount = operators.filter((o) => o.type === 'spindle' && o.status === 'active').length
  const conningCount = operators.filter((o) => o.type === 'conning' && o.status === 'active').length
  const isPanelOpen = !!editing

  if (loading) return <div className="p-8 text-center text-gray-400">Loading operators...</div>

  return (
    <div className="space-y-4">
      <div className="min-w-0 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-t-4 border-gray-100 border-t-brand-600 rounded-xl p-4">
              <p className="stat-label">Spindle Operators</p>
              <p className="stat-val">{spindleCount} <span className="text-sm font-normal text-gray-400">active</span></p>
            </div>
            <div className="bg-white border border-t-4 border-gray-100 border-t-teal-500 rounded-xl p-4">
              <p className="stat-label">Conning Operators</p>
              <p className="stat-val">{conningCount} <span className="text-sm font-normal text-gray-400">active</span></p>
            </div>
          </div>

          <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or unit...">
            <Button onClick={startNew}>{isPanelOpen ? '+ New Operator' : '+ Add Operator'}</Button>
          </SearchBar>

          <Tabs tabs={['All', 'Spindle', 'Conning']} active={tab} onChange={setTab} />

          <Card bodyClass="!p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Unit</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-sm">No operators found</td></tr>
                )}
                {visible.map((o) => (
                  <tr key={o.id} className={o.status === 'inactive' ? 'opacity-50' : ''}>
                    <td className="font-medium">{o.name}</td>
                    <td><Badge variant={TYPE_BADGE[o.type]}>{TYPE_LABEL[o.type]}</Badge></td>
                    <td className="text-gray-500 text-xs">{o.unit?.name || '-'}</td>
                    <td className="td-mono text-gray-600">{o.mobile || '-'}</td>
                    <td>
                      <button
                        onClick={() => toggleStatus(o.id)}
                        className={`text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors ${
                          o.status === 'active'
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {o.status === 'active' ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-1 pr-2">
                        <button 
                          onClick={() => startEdit(o)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          onClick={() => setDeletingId(o.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

      {editing && (
        <Modal
          isOpen={!!editing}
          onClose={cancel}
          title={editing === 'new' ? 'Add New Operator' : 'Edit Operator Details'}
          size="md"
          confirmClose={form.name.trim() !== ''}
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Operator Name" value={form.name} onChange={f('name')} placeholder="Full name" className="rounded-xl" />
              <Input
                label="Mobile Number"
                value={form.mobile}
                onChange={f('mobile')}
                type="tel"
                placeholder="10-digit mobile"
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase mb-2 block ml-1">Work Assignment</label>
              <div className="grid grid-cols-2 gap-3">
                {['spindle', 'conning'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, type: t }))}
                    className={`py-4 rounded-2xl border-2 text-sm font-black capitalize transition-all ${
                      form.type === t
                        ? t === 'spindle' ? 'bg-brand-50 text-brand-700 border-brand-600 shadow-sm scale-[1.02]' : 'bg-teal-50 text-teal-700 border-teal-600 shadow-sm scale-[1.02]'
                        : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    {t} Loading
                  </button>
                ))}
              </div>
            </div>

            <Select
              label="Assigned Production Unit"
              value={form.unitId}
              onChange={f('unitId')}
              options={units.map((u) => ({ label: u.name, value: u.id }))}
              className="rounded-xl"
            />

            <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl text-[11px] text-gray-500 leading-relaxed">
              <strong>Tip:</strong> Correct assignment ensures work records and production reporting are attributed to the right unit and operator type.
            </div>

            <div className="pt-4">
              <Button onClick={save} disabled={acting || !form.name.trim()} className="w-full h-14 rounded-2xl font-bold shadow-lg shadow-brand-100 bg-brand-600 text-white">
                {acting ? 'Saving...' : (editing === 'new' ? 'Create Operator' : 'Save Changes')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => { setDeletingId(null); setError(''); }}
        onConfirm={handleDelete}
        title="Delete Operator?"
        message="This will permanently remove this operator. This action cannot be undone."
        confirmText="Yes, Delete"
        isLoading={acting}
        error={error}
      />
    </div>
  )
}
